import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import loginUseCase from '@/application/auth/login';
import DLoading from '@/components/DLoading';
import { DEMO_ACCOUNT_EMAIL, DEMO_ACCOUNT_PASSWORD } from '@/constants/demoAccount';
import useToast from '@/hooks/useToast';
import mixpanel from '@/utils/mixpanel';

const isCaptchaEnabled = () => {
  const siteKey = import.meta.env.VITE_APP_TURNSTILE_SITEKEY;
  return siteKey && siteKey !== 'YOUR_SITE_KEY_HERE' && siteKey !== 'DISABLED';
};

/**
 * Bucketeer `onboarding-demo-account` = demo-account: signs in with the shared demo user
 * and sends the user to the app home (same outcome as "Try with a demo account" on v2 onboarding).
 */
const CreateAccountDemoAutoLogin = () => {
  const navigate = useNavigate();
  const { addErrorToast } = useToast();
  const captchaEnabled = isCaptchaEnabled();
  const [captchaToken, setCaptchaToken] = useState('');
  const loginStartedRef = useRef(false);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (captchaEnabled) return;

    const run = async () => {
      if (loginStartedRef.current) return;
      loginStartedRef.current = true;

      mixpanel.track('Onboarding Demo Account Login Started', {
        page: 'url_entry',
        step: 1,
        entry: 'create_account_bucket_auto',
      });

      try {
        const result = await loginUseCase({
          username: DEMO_ACCOUNT_EMAIL,
          password: DEMO_ACCOUNT_PASSWORD,
        });

        if (result?.status === 200) {
          mixpanel.track('Onboarding Demo Account Login Succeeded', {
            page: 'url_entry',
            step: 1,
            entry: 'create_account_bucket_auto',
          });
          navigate('/');
          return;
        }

        if (result?.status === 428) {
          addErrorToast({
            message: 'This account requires two-factor authentication. Please sign in from the login page.',
          });
          return;
        }

        if (result?.status === 412) {
          addErrorToast({ message: 'Unable to sign in with the demo account. Please try again.' });
          return;
        }

        if (result?.response?.status === 401) {
          addErrorToast({ message: 'Invalid demo account credentials.' });
          return;
        }

        const errorMessage =
          result?.response?.data?.detail || result?.message || 'Demo sign-in failed. Please try again.';
        addErrorToast({ message: errorMessage });
      } catch (err) {
        loginStartedRef.current = false;
        console.error(err);
        addErrorToast({ message: err?.response?.data?.detail || 'Demo sign-in failed. Please try again.' });
      }
    };

    run();
  }, [captchaEnabled, navigate, addErrorToast]);

  useEffect(() => {
    if (!captchaEnabled || !captchaToken) return;

    const run = async () => {
      if (loginStartedRef.current) return;
      loginStartedRef.current = true;

      mixpanel.track('Onboarding Demo Account Login Started', {
        page: 'url_entry',
        step: 1,
        entry: 'create_account_bucket_auto',
      });

      try {
        const result = await loginUseCase({
          username: DEMO_ACCOUNT_EMAIL,
          password: DEMO_ACCOUNT_PASSWORD,
          captcha_token: captchaToken || '',
        });

        if (result?.status === 200) {
          mixpanel.track('Onboarding Demo Account Login Succeeded', {
            page: 'url_entry',
            step: 1,
            entry: 'create_account_bucket_auto',
          });
          navigate('/');
          return;
        }

        const errorDetail = (result?.response?.data?.detail || '').toString();
        if (
          result?.response?.status === 400 &&
          (errorDetail.toLowerCase().includes('captcha') ||
            errorDetail.toLowerCase().includes('invalid captcha') ||
            errorDetail.toLowerCase().includes('captcha verification failed'))
        ) {
          loginStartedRef.current = false;
          try {
            captchaRef.current?.reset?.();
          } catch {
            /* noop */
          }
          setCaptchaToken('');
          addErrorToast({ message: 'CAPTCHA verification failed. Please refresh the page.' });
          return;
        }

        if (result?.status === 428) {
          addErrorToast({
            message: 'This account requires two-factor authentication. Please sign in from the login page.',
          });
          return;
        }

        if (result?.status === 412) {
          addErrorToast({ message: 'Unable to sign in with the demo account. Please try again.' });
          return;
        }

        if (result?.response?.status === 401) {
          addErrorToast({ message: 'Invalid demo account credentials.' });
          return;
        }

        const errorMessage =
          result?.response?.data?.detail || result?.message || 'Demo sign-in failed. Please try again.';
        addErrorToast({ message: errorMessage });
      } catch (err) {
        loginStartedRef.current = false;
        console.error(err);
        addErrorToast({ message: err?.response?.data?.detail || 'Demo sign-in failed. Please try again.' });
      }
    };

    run();
  }, [captchaEnabled, captchaToken, navigate, addErrorToast]);

  return (
    <div className="relative min-h-[50vh] flex flex-col items-center justify-center">
      <DLoading />
      {captchaEnabled && (
        <div className="fixed left-0 top-0 -z-10 opacity-0 pointer-events-none w-px h-px overflow-hidden" aria-hidden>
          <Turnstile
            ref={captchaRef}
            siteKey={import.meta.env.VITE_APP_TURNSTILE_SITEKEY}
            options={{
              action: 'onboarding',
              cData: 'onboarding',
              theme: 'light',
              size: 'invisible',
            }}
            onSuccess={(token) => {
              setCaptchaToken(token);
            }}
            onError={() => {
              setCaptchaToken('');
              addErrorToast({ message: 'CAPTCHA verification failed. Please refresh the page.' });
            }}
            onExpire={() => {
              setCaptchaToken('');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CreateAccountDemoAutoLogin;
